import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  InputBase,
  Popper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import moment from 'moment';
import { tokens } from '../../../global/theme/tokens';
import {
  DATE_PRESETS,
  SORT_OPTIONS,
  matchDatePreset,
} from './workTimeReportUtils';
import WorkTimeReportDateRange from './WorkTimeReportDateRange';

/* -------------------------------------------------------------------------- */
/*  Date Range Trigger (text button + popper with presets + custom inputs)    */
/* -------------------------------------------------------------------------- */
const DateRangeTrigger = ({ startDate, endDate, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const matched = matchDatePreset(startDate, endDate);
  const label = matched
    ? t(matched.labelKey)
    : `${moment(startDate).format('DD.MM.YY')} – ${moment(endDate).format('DD.MM.YY')}`;

  const handlePresetClick = (preset) => {
    const { start, end } = preset.get();
    onChange({
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
    });
    setOpen(false);
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition-colors"
        sx={{
          '&:hover': { bgcolor: colors.card },
        }}
      >
        <CalendarTodayIcon sx={{ fontSize: 14, opacity: 0.6 }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: 16, opacity: 0.5 }} />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 9999, paddingTop: '6px' }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            className="rounded-lg backdrop-blur-lg p-2 flex flex-col gap-2 min-w-[260px]"
            sx={{ bgcolor: colors.cardSolid, boxShadow: 3 }}
          >
            <Box className="flex flex-col">
              {DATE_PRESETS.map((preset) => {
                const isActive = matched?.key === preset.key;
                return (
                  <Box
                    key={preset.key}
                    onClick={() => handlePresetClick(preset)}
                    className="px-2 py-1 rounded cursor-pointer transition-colors"
                    sx={{
                      bgcolor: isActive ? `${colors.info}22` : 'transparent',
                      color: isActive ? colors.info : colors.text,
                      fontWeight: isActive ? 600 : 400,
                      '&:hover': { bgcolor: colors.card },
                    }}
                  >
                    <Typography variant="body2">{t(preset.labelKey)}</Typography>
                  </Box>
                );
              })}
            </Box>

            <Box
              className="border-t pt-2 flex flex-col gap-2"
              sx={{ borderColor: colors.card }}
            >
              <Typography variant="caption" sx={{ opacity: 0.7, pl: 0.5 }}>
                {t('Custom range')}
              </Typography>
              <WorkTimeReportDateRange
                startDate={startDate}
                endDate={endDate}
                onChange={(next) => {
                  onChange(next);
                }}
              />
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*  Sort Trigger (text button + popper with sort options)                     */
/* -------------------------------------------------------------------------- */
const SortTrigger = ({ sortKey, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const active = SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0];

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition-colors"
        sx={{
          '&:hover': { bgcolor: colors.card },
        }}
      >
        <SwapVertIcon sx={{ fontSize: 14, opacity: 0.6 }} />
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {t('Sort by')}:
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t(active.labelKey)} {active.dir === 'desc' ? '↓' : '↑'}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: 16, opacity: 0.5 }} />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 9999, paddingTop: '6px' }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            className="rounded-lg backdrop-blur-lg p-2 flex flex-col min-w-[220px]"
            sx={{ bgcolor: colors.cardSolid, boxShadow: 3 }}
          >
            {SORT_OPTIONS.map((opt) => {
              const isActive = opt.key === sortKey;
              return (
                <Box
                  key={opt.key}
                  onClick={() => {
                    onChange(opt.key);
                    setOpen(false);
                  }}
                  className="px-2 py-1 rounded cursor-pointer transition-colors flex items-center justify-between gap-3"
                  sx={{
                    bgcolor: isActive ? `${colors.info}22` : 'transparent',
                    color: isActive ? colors.info : colors.text,
                    fontWeight: isActive ? 600 : 400,
                    '&:hover': { bgcolor: colors.card },
                  }}
                >
                  <Typography variant="body2">{t(opt.labelKey)}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    {opt.dir === 'desc' ? '↓' : '↑'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*  Expanding Search Field (icon → expands to InputBase on click)             */
/* -------------------------------------------------------------------------- */
const ExpandingSearch = ({ value, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(Boolean(value));

  useEffect(() => {
    if (value && !expanded) setExpanded(true);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    setExpanded(false);
    onChange('');
  };

  return (
    <Box
      className="flex items-center overflow-hidden transition-all duration-300"
      sx={{
        width: expanded ? 220 : 32,
        minWidth: 32,
        bgcolor: expanded ? colors.bgInput : 'transparent',
        borderRadius: '8px',
        border: expanded ? `1px solid ${colors.info}` : '1px solid transparent',
        '&:hover': {
          bgcolor: expanded ? colors.bgInput : colors.card,
        },
      }}
    >
      {expanded ? (
        <>
          <SearchIcon sx={{ fontSize: 18, ml: 1, opacity: 0.5 }} />
          <InputBase
            placeholder={t('Search employee')}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') close();
            }}
            autoFocus
            className="flex-1 px-2"
            sx={{ fontSize: 13 }}
          />
          <IconButton size="small" onClick={close}>
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </>
      ) : (
        <IconButton
          size="small"
          onClick={() => setExpanded(true)}
          sx={{ width: '100%', borderRadius: '8px' }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Main Toolbar                                                              */
/* -------------------------------------------------------------------------- */
const WorkTimeReportToolbar = ({
  startDate,
  endDate,
  onDateRangeChange,
  search,
  onSearchChange,
  sortKey,
  onSortChange,
  onExportCsv,
  hasData,
}) => {
  const { t } = useTranslation();

  return (
    <Box className="flex items-center gap-2 flex-wrap flex-1">
      <DateRangeTrigger
        startDate={startDate}
        endDate={endDate}
        onChange={onDateRangeChange}
      />
      <SortTrigger sortKey={sortKey} onChange={onSortChange} />
      <Box className="flex-1" />
      <ExpandingSearch value={search} onChange={onSearchChange} />
      <Tooltip title={t('Export CSV')}>
        <span>
          <IconButton size="small" onClick={onExportCsv} disabled={!hasData}>
            <DownloadIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default WorkTimeReportToolbar;
